import type { ReactNode } from 'react';
import { Avatar } from '@/components/ui/avatar';
import type { OrderSource } from '@api/orderSources';
import type { Salesperson } from './types';
import { useTranslation } from '@hooks/useTranslation';
import { sanitizeImageUrl } from '../../utils/url';

interface SalespersonSourceCardProps {
  salesperson: Salesperson | null;
  orderSource: OrderSource | null;
  referredBy?: string | null;
  /**
   * The payment badge, which sits at the end of the source name line. Passed in
   * rather than rendered here so this component keeps knowing only about the
   * salesperson and the source.
   */
  paymentBadge?: ReactNode;
}

export function SalespersonSourceCard({
  salesperson,
  orderSource,
  referredBy,
  paymentBadge,
}: SalespersonSourceCardProps) {
  const { t } = useTranslation();
  const avatarUrl = sanitizeImageUrl(salesperson?.avatar_url);

  return (
    <div className="grid grid-cols-1 gap-6 md:grid-cols-2 md:gap-8">
      <div className="space-y-4">
        <h3 className="font-inter text-lg font-semibold leading-normal text-off-black">
          {t('salesperson_card.salesperson_title')}
        </h3>
        <div className="flex items-center gap-4">
          <Avatar size="lg" src={avatarUrl ?? undefined} name={salesperson?.name ?? undefined} />
          {/* Let long referral values shrink and wrap within this flex item. */}
          <div className="min-w-0 space-y-1">
            <p className="font-inter text-sm font-semibold leading-tight text-off-black">
              {salesperson?.name || '—'}
            </p>
            {referredBy && (
              <p className="break-words font-inter text-xs font-normal leading-tight text-off-black">
                {t('salesperson_card.referred_by')} {referredBy}
              </p>
            )}
          </div>
        </div>
      </div>

      <div className="space-y-4">
        <h3 className="font-inter text-lg font-semibold leading-normal text-off-black">
          {t('salesperson_card.source_title')}
        </h3>
        <div className="space-y-1">
          {/* The badge rides the name line, and wraps below it rather than
              overflowing the column when the source name runs long. */}
          <div className="flex flex-wrap items-center gap-3">
            {orderSource ? (
              <p className="font-inter text-sm font-semibold leading-tight text-off-black">
                {orderSource.name}
              </p>
            ) : (
              <span className="font-inter text-sm font-normal leading-tight text-off-black">—</span>
            )}
            {paymentBadge}
          </div>
          {orderSource && (orderSource.fee_type || orderSource.fee != null) && (
            <p className="font-inter text-xs font-normal leading-tight text-off-black flex items-center gap-8">
              <span>Fee type: {orderSource.fee_type || '—'}</span>
              <span>Value: {orderSource.fee != null ? orderSource.fee.toFixed(1) : '—'}</span>
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
